import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Phone, Mail, CalendarClock } from "lucide-react";
import { PHONE, PHONE_HREF, EMAIL } from "@/lib/api";

const Option = ({ icon: I, label, value, href, onClick, testid, accent }) => (
  <a href={href} onClick={onClick} data-testid={testid}
    className={`group flex items-center gap-3 rounded-2xl p-3 transition-colors duration-200 ${accent ? "bg-lime-400 hover:bg-lime-300" : "hover:bg-[#EAF3FF]"}`}>
    <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${accent ? "bg-[#050B1F] text-lime-300" : "bg-[#0B2A6F] text-lime-300"}`}><I className="h-5 w-5" /></span>
    <span className="min-w-0">
      <span className="block text-sm font-bold text-[#050B1F]">{label}</span>
      <span className="block truncate text-xs text-slate-700">{value}</span>
    </span>
  </a>
);

const scrollToBooking = () => {
  const el = document.getElementById("book-call");
  if (!el) return false;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -100 });
  else el.scrollIntoView({ behavior: "smooth" });
  return true;
};

export const TalkBubble = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (!search.includes("book=1")) return undefined;
    const t = setTimeout(scrollToBooking, 700);
    return () => clearTimeout(t);
  }, [pathname, search]);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, []);

  const book = (e) => {
    e.preventDefault();
    setOpen(false);
    if (!scrollToBooking()) navigate("/contact?book=1");
  };

  return (
    <div ref={ref} className="fixed bottom-4 right-4 z-50 flex flex-col items-end sm:bottom-6 sm:right-6" data-testid="talk-bubble">
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 16, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 320, damping: 26 }} data-testid="talk-bubble-panel"
            className="mb-3 w-[min(20rem,calc(100vw-2rem))] origin-bottom-right rounded-3xl border border-[#0B2A6F]/10 bg-white p-3 shadow-[0_30px_80px_-20px_rgba(5,11,31,0.5)]">
            <div className="rounded-2xl bg-[#050B1F] p-4 text-white">
              <p className="font-display text-lg font-extrabold">Talk to a real person</p>
              <p className="mt-1 text-xs text-blue-100/85">Pick how you'd like to reach us.</p>
            </div>
            <div className="mt-2 space-y-1">
              <Option icon={Phone} label="Call us" value={PHONE} href={PHONE_HREF} testid="talk-call-link" />
              <Option icon={Mail} label="Email us" value={EMAIL} href={`mailto:${EMAIL}`} testid="talk-email-link" />
              <Option icon={CalendarClock} label="Book a call" value="Pick a time that works for you" href="#book-call" onClick={book} testid="talk-book-link" accent />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.button onClick={() => setOpen((v) => !v)} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} aria-expanded={open}
        data-testid="talk-bubble-toggle"
        className="relative inline-flex items-center gap-2 rounded-full bg-[#0B2A6F] py-3.5 pl-4 pr-5 font-bold text-white shadow-[0_20px_50px_-15px_rgba(11,42,111,0.8)]">
        {!open && <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-lime-400/40" />}
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5 text-lime-300" />}
        <span className="text-sm">{open ? "Close" : "Talk to us"}</span>
      </motion.button>
    </div>
  );
};
